import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Headset, Image as ImageIcon, Languages, ListOrdered, Megaphone, RotateCcw, Search, Smartphone, Star, UserRound,
} from 'lucide-react';
import HeaderActions from '../../../../components/FooterSettings/HeaderActionBar';
import {
  AccountSection,
  AnnouncementSection,
  CustomMenuSection,
  DestinationsMenuSection,
  HeaderPreview,
  HolidayTypesMenuSection,
  LanguageSection,
  LogoConfigSection,
  MenuOrderSection,
  MobileMenuSection,
  ReviewsSection,
  SearchSection,
  SupportSection,
} from '../../../../components/HeaderSettings';
import { HeaderEditorContext } from '../../../../components/HeaderSettings/editorControls';
import { menuCardId, menuEntries } from '../../../../components/HeaderSettings/MenuOrderSection';
import { SECTION, validateSiteHeader } from '../../../../components/HeaderSettings/siteHeaderModel';
import { pickText } from '../../../../components/HeaderSettings/headerDefaults';
import BaseModal from '../../../../components/BaseComponents/BaseModal/BaseModal';
import { useToast } from '../../../../context/ToastContext';
import { useHeaderConfig } from '../../../../hooks/useHeaderConfig';
import { useHeaderLookups } from './useHeaderLookups';
import pageStyles from '../../../../components/HeaderSettings/HeaderSettings.module.css';
import styles from '../../../../components/HeaderSettings/HeaderEditor.module.css';
import '../../../../styles/cms-form-controls.css';

// CMS -> Layout -> Header: every element of the website header in one place, with a live
// preview of the bar above the cards. The cards follow the bar: logo, announcement strip, the
// dropdown menus (in their order), search, customer service, reviews, language, account, and
// the phone menu.

/** The card an error belongs to, so a failed save can scroll to it. */
const cardForPath = (path) => {
  if (path.startsWith('menus.destinations')) return 'menu-destinations';
  if (path.startsWith('menus.holidayTypes')) return 'menu-holidayTypes';
  const custom = path.match(/^menus\.custom\.([^.]+)/);
  if (custom) return `menu-${custom[1]}`;
  return null;
};

const scrollToCard = (id) => {
  document.getElementById(`header-section-${id}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
};

export default function HeaderSettings() {
  const { showToast } = useToast();
  const [lang, setLang] = useState('nl');
  const [showErrors, setShowErrors] = useState(false);
  const [focus, setFocus] = useState(null);
  const [pinned, setPinned] = useState(true);
  const [dockHeight, setDockHeight] = useState(0);
  const [activeSection, setActiveSection] = useState(SECTION.logo);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [errorJump, setErrorJump] = useState(0);
  const dockRef = useRef(null);

  const {
    config,
    loading,
    saving,
    loadError,
    loaded,
    isDirty,
    updateField,
    setSiteHeaderPath,
    patchSiteHeader,
    load,
    save,
    reset,
  } = useHeaderConfig({
    onLoadError: (err) => showToast(err.response?.data?.message || 'Failed to load header settings', 'error'),
  });
  const { holidayTypes, typesLoading, homepageGroups, homepageLoading } = useHeaderLookups({
    onError: (message) => showToast(message, 'error'),
  });

  const sh = config.siteHeader;
  const errors = useMemo(() => validateSiteHeader(sh), [sh]);
  const errorMap = useMemo(() => {
    const map = new Map();
    errors.forEach((e) => { if (!map.has(e.path)) map.set(e.path, e.message); });
    return map;
  }, [errors]);
  // Shown only once a save has been tried, so a half-typed row is not red while it is typed.
  const visibleErrors = showErrors ? errors : [];
  const countFor = (test) => visibleErrors.filter(test).length;
  const sectionErrors = (section) => countFor((e) => e.section === section && !cardForPath(e.path));
  const cardErrors = (cardId) => countFor((e) => cardForPath(e.path) === cardId);

  const focusPreview = useCallback((target) => setFocus({ target, at: Date.now() }), []);
  const editor = useMemo(
    () => ({ lang, showErrors, errors: errorMap, holidayTypes, typesLoading, focusPreview }),
    [lang, showErrors, errorMap, holidayTypes, typesLoading, focusPreview]
  );

  const entries = menuEntries(sh);

  // The dock's height, so a card scrolled to lands below the pinned preview, not under it.
  useLayoutEffect(() => {
    const el = dockRef.current;
    if (!el) return undefined;
    const measure = () =>
      setDockHeight(el.getBoundingClientRect().height - (parseFloat(getComputedStyle(el).paddingTop) || 0));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  // Which card is in view, for the section list.
  useEffect(() => {
    if (loading || typeof IntersectionObserver === 'undefined') return undefined;
    const nodes = [...document.querySelectorAll('[id^="header-section-"]')];
    const visible = new Map();
    const io = new IntersectionObserver(
      (records) => {
        records.forEach((r) => {
          if (r.isIntersecting) visible.set(r.target.id, r.boundingClientRect.top);
          else visible.delete(r.target.id);
        });
        const top = [...visible.entries()].sort((a, b) => a[1] - b[1])[0];
        if (top) setActiveSection(top[0].replace('header-section-', ''));
      },
      { rootMargin: `-${Math.round((pinned ? dockHeight : 0) + 8)}px 0px -55% 0px` }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [loading, pinned, dockHeight, entries.length]);

  // After a refused save: bring the first problem into view and put the cursor in it.
  useEffect(() => {
    if (!errorJump || !errors.length) return;
    const first = errors[0];
    scrollToCard(cardForPath(first.path) || first.section);
    const t = setTimeout(() => {
      document.querySelector('[aria-invalid="true"]')?.focus({ preventScroll: true });
    }, 350);
    return () => clearTimeout(t);
    // Only on a new attempt, not on every edit that follows it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorJump]);

  const handleSave = async () => {
    if (!loaded) {
      showToast('The saved header settings have not loaded, so saving now would replace them with defaults. Try loading them again first.', 'error');
      return;
    }
    if (errors.length) {
      setShowErrors(true);
      setErrorJump((n) => n + 1);
      const more = errors.length > 1 ? ` (${errors.length - 1} more to fix)` : '';
      showToast(`Not saved. ${errors[0].message}${more}`, 'error');
      return;
    }
    try {
      await save();
      setShowErrors(false);
      showToast('Header settings saved successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save header settings', 'error');
    }
  };

  const discard = async (message) => {
    try {
      await reset();
      setShowErrors(false);
      showToast(message, 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to reload header settings', 'error');
    }
  };

  const handleCancel = () => {
    if (!isDirty) {
      showToast('Nothing to discard: everything is saved', 'success');
      return;
    }
    discard('Unsaved changes discarded');
  };

  const handleResetConfirm = () => {
    setShowResetConfirm(false);
    discard('Header settings reset to the last save');
  };

  const handleRetry = async () => {
    try {
      await load();
      showToast('Header settings loaded', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load header settings', 'error');
    }
  };

  if (loading) {
    return (
      <div className={`${pageStyles.page} cms-form-scope`}>
        <div className={pageStyles.pageHeader}>
          <div className={pageStyles.headerLeft}>
            <div className={pageStyles.headerIcon}>≡</div>
            <div>
              <h1>CMS - Header Configuration</h1>
              <p>Loading…</p>
            </div>
          </div>
        </div>
        <p style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
          Loading header configuration…
        </p>
      </div>
    );
  }

  const anyMenuOn = entries.some((e) => e.menu.enabled);
  const nav = [
    { id: SECTION.logo, label: 'Logo', icon: ImageIcon, on: true },
    { id: SECTION.announcement, label: 'Announcement bar', icon: Megaphone, on: sh.announcement.enabled, errors: sectionErrors(SECTION.announcement) },
    { id: SECTION.menus, label: 'Menus', icon: ListOrdered, on: anyMenuOn, errors: 0 },
    ...entries.map((e) => ({
      id: menuCardId(e.key),
      label: pickText(e.menu.label, lang, e.fallback) || 'Untitled menu',
      on: e.menu.enabled,
      sub: true,
      errors: cardErrors(menuCardId(e.key)),
    })),
    { id: SECTION.search, label: 'Search', icon: Search, on: sh.search.enabled },
    { id: SECTION.support, label: 'Customer service', icon: Headset, on: sh.support.enabled, errors: sectionErrors(SECTION.support) },
    { id: SECTION.reviews, label: 'Reviews', icon: Star, on: sh.trustpilot.enabled },
    { id: SECTION.language, label: 'Language switch', icon: Languages, on: sh.language.enabled },
    { id: SECTION.account, label: 'Account', icon: UserRound, on: sh.account.enabled, errors: sectionErrors(SECTION.account) },
    { id: SECTION.mobile, label: 'Mobile menu', icon: Smartphone, on: true, errors: sectionErrors(SECTION.mobile) },
  ];

  const renderMenuCard = (e, index) => {
    const cardId = menuCardId(e.key);
    if (e.kind === 'destinations') {
      return (
        <DestinationsMenuSection
          key={e.key}
          value={sh.menus.destinations}
          set={setSiteHeaderPath}
          patch={patchSiteHeader}
          homepageGroups={homepageGroups}
          homepageLoading={homepageLoading}
          errorCount={cardErrors(cardId)}
        />
      );
    }
    if (e.kind === 'holidayTypes') {
      return (
        <HolidayTypesMenuSection
          key={e.key}
          value={sh.menus.holidayTypes}
          set={setSiteHeaderPath}
          patch={patchSiteHeader}
          errorCount={cardErrors(cardId)}
        />
      );
    }
    return (
      <CustomMenuSection
        key={e.key}
        menu={e.menu}
        index={sh.menus.custom.findIndex((m) => m.id === e.menu.id)}
        set={setSiteHeaderPath}
        errorCount={cardErrors(cardId)}
      />
    );
  };

  return (
    <HeaderEditorContext.Provider value={editor}>
      <div className={`${pageStyles.page} cms-form-scope`}>
        <div className={pageStyles.pageHeader}>
          <div className={pageStyles.headerLeft}>
            <div className={pageStyles.headerIcon}>≡</div>
            <div>
              <h1>CMS - Header Configuration</h1>
              <p>Every part of the website header, in one place. The preview shows your changes as you make them.</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            {isDirty && <span className={styles.unsaved}>Unsaved changes</span>}
            <button type="button" className={pageStyles.resetBtn} onClick={() => setShowResetConfirm(true)}>
              <RotateCcw size={14} />
              Reset All
            </button>
          </div>
        </div>

        <BaseModal
          open={showResetConfirm}
          onClose={() => setShowResetConfirm(false)}
          title="Reset header settings"
          width="440px"
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                type="button"
                onClick={() => setShowResetConfirm(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: 10,
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#374151',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetConfirm}
                style={{
                  padding: '10px 20px',
                  borderRadius: 10,
                  border: 'none',
                  background: '#f5a51e',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Reset
              </button>
            </div>
          }
        >
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: '#374151' }}>
            Reset all header settings to last saved? Your unsaved changes will be discarded.
          </p>
        </BaseModal>

        {loadError && (
          <div className={styles.errorBanner}>
            {loadError}{' '}
            <button type="button" className={styles.ghostBtn} onClick={handleRetry} style={{ marginLeft: 8 }}>
              Try again
            </button>
          </div>
        )}

        {/* The preview spans the full width (the bar is wide), above the section list and cards. */}
        <div className={styles.editor} style={{ '--header-dock-h': `${pinned ? dockHeight : 0}px` }}>
          <div ref={dockRef} className={`${styles.dockWrap} ${pinned ? styles.dockPinned : ''}`}>
            <HeaderPreview
              config={config}
              homepageGroups={homepageGroups}
              holidayTypes={holidayTypes}
              lang={lang}
              onLang={setLang}
              focus={focus}
              pinned={pinned}
              onPinned={setPinned}
            />
          </div>

          <div className={styles.layout}>
            <nav className={styles.nav} aria-label="Header sections">
              <span className={styles.navTitle}>Sections</span>
              {nav.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`${styles.navItem} ${item.sub ? styles.navSub : ''} ${activeSection === item.id ? styles.navItemActive : ''}`}
                    onClick={() => {
                      setActiveSection(item.id);
                      scrollToCard(item.id);
                    }}
                  >
                    {Icon && <Icon size={15} aria-hidden="true" />}
                    <span className={styles.navLabel}>{item.label}</span>
                    {item.errors > 0
                      ? <span className={styles.navErr} title={`${item.errors} to fix`}>{item.errors}</span>
                      : <span className={`${styles.navDot} ${item.on ? styles.navDotOn : ''}`} title={item.on ? 'On' : 'Off'} />}
                  </button>
                );
              })}
            </nav>

            <div className={styles.main}>
              <div id={`header-section-${SECTION.logo}`} className={styles.anchor}>
                <LogoConfigSection
                  logoUrl={config.logoUrl}
                  logoAltText={config.logoAltText}
                  logoLinkTarget={config.logoLinkTarget}
                  onFieldChange={updateField}
                  wordmark={sh.wordmark}
                  onWordmarkChange={(p) => patchSiteHeader('wordmark', p)}
                  logoHeight={sh.logo?.height ?? null}
                  onLogoHeightChange={(height) => patchSiteHeader('logo', { height })}
                />
              </div>

              <AnnouncementSection
                value={sh.announcement}
                onPatch={(p) => patchSiteHeader('announcement', p)}
                errorCount={sectionErrors(SECTION.announcement)}
              />

              <MenuOrderSection siteHeader={sh} set={setSiteHeaderPath} onJump={(id) => setTimeout(() => scrollToCard(id), 60)} />
              {entries.map(renderMenuCard)}

              <SearchSection value={sh.search} onPatch={(p) => patchSiteHeader('search', p)} />
              <SupportSection
                value={sh.support}
                onPatch={(p) => patchSiteHeader('support', p)}
                errorCount={sectionErrors(SECTION.support)}
              />
              <ReviewsSection value={sh.trustpilot} onPatch={(p) => patchSiteHeader('trustpilot', p)} />
              <LanguageSection value={sh.language} onPatch={(p) => patchSiteHeader('language', p)} />
              <AccountSection
                value={sh.account}
                onPatch={(p) => patchSiteHeader('account', p)}
                errorCount={sectionErrors(SECTION.account)}
              />
              <MobileMenuSection
                value={sh.mobileMenu}
                set={setSiteHeaderPath}
                patch={patchSiteHeader}
                errorCount={sectionErrors(SECTION.mobile)}
              />
            </div>
          </div>
        </div>

        <HeaderActions
          saved={!isDirty && !saving}
          saving={saving}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      </div>
    </HeaderEditorContext.Provider>
  );
}
